import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { useAuth } from '@/providers/AuthProvider';
import { OriginalPages } from '@/components/OriginalPages';
import { AnnotatedTranscript, AnnotationList, type EssayAnnotation } from '@/components/AnnotatedTranscript';
import { getQuestionTypeLabel } from '@/lib/questionTypeLabels';
import { QuestionFeedbackChip } from '@/components/QuestionFeedbackChip';
import { PostReviewSurvey } from '@/components/PostReviewSurvey';
import type { SubmissionQuestionResult } from '@/types/grading';

interface ExtractedQuestion {
  questionId: string;
  questionText: string;
  questionType: string;
  sectionInstruction?: string;
}

interface SubmissionData {
  id: string;
  student_name: string | null;
  student_class: string | null;
  submitted_at: string;
  has_pending_review: boolean;
  /** Uploaded pages (images or PDF) — the student's original handwriting */
  image_paths?: string[] | null;
  grading_result: {
    questions: SubmissionQuestionResult[];
    totalMarks: number;
    maxTotalMarks: number;
    gradedAt: string;
  } | null;
}

interface AssignmentData {
  id: string;
  grade_level: string;
  wizard_data: {
    extractedQuestions?: ExtractedQuestion[];
  } | null;
}

function renderComment(text: string | null | undefined): React.ReactNode {
  if (!text) return null;
  const paragraphs = text.split(/\n\n+/);
  if (paragraphs.length <= 1) {
    return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
  }
  return (
    <>
      {paragraphs.map((para, i) => {
        const match = para.match(/^(【[^】]+】|綜合而言)/);
        if (match) {
          const header = match[1] ?? '';
          const rest = para.slice(header.length);
          return (
            <Typography key={i} variant="body2" sx={{ mb: i < paragraphs.length - 1 ? 1 : 0, whiteSpace: 'pre-wrap' }}>
              <strong>{header}</strong>{rest}
            </Typography>
          );
        }
        return (
          <Typography key={i} variant="body2" sx={{ mb: i < paragraphs.length - 1 ? 1 : 0, whiteSpace: 'pre-wrap' }}>
            {para}
          </Typography>
        );
      })}
    </>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('zh-HK', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface QuestionCardProps {
  question: SubmissionQuestionResult;
  questionIndex: number;
  questionData?: ExtractedQuestion;
  /** Pages of the student's original upload — shown beside essay transcripts */
  originalPaths: string[];
  submissionId: string;
  assignmentId: string;
  onSave: (index: number, score: number, feedback: string) => Promise<void>;
  saving: boolean;
}

function QuestionCard({ question, questionIndex, questionData, originalPaths, submissionId, assignmentId, onSave, saving }: QuestionCardProps) {
  const annotations = ((question as any).annotations ?? []) as EssayAnnotation[];
  const isEssay = questionData?.questionType === 'essay';
  const [editScore, setEditScore] = useState<string>(() =>
    String(question.teacher_score != null ? question.teacher_score : question.marksAwarded),
  );
  const [editFeedback, setEditFeedback] = useState<string>(
    question.teacher_feedback ?? question.feedback ?? '',
  );
  const [localSaving, setLocalSaving] = useState(false);

  const isReviewed = !question.needs_review || question.reviewed_at != null;
  const needsReview = question.needs_review === true && question.reviewed_at == null;

  const effectiveScore = question.teacher_score != null ? question.teacher_score : question.marksAwarded;

  const handleSave = async () => {
    const score = parseFloat(editScore);
    if (isNaN(score) || score < 0 || score > question.maxMarks) return;
    setLocalSaving(true);
    try {
      await onSave(questionIndex, score, editFeedback);
    } finally {
      setLocalSaving(false);
    }
  };

  const borderColor = needsReview
    ? 'warning.main'
    : isReviewed && question.reviewed_at
    ? 'success.main'
    : 'divider';

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor,
        borderWidth: needsReview ? 2 : 1,
        mb: 2,
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {question.questionId}
          </Typography>
          {questionData && (
            <Chip
              label={getQuestionTypeLabel(questionData.questionType)}
              size="small"
              variant="outlined"
            />
          )}
          {needsReview && (
            <Chip
              icon={<RateReviewIcon />}
              label="需要審核"
              color="warning"
              size="small"
            />
          )}
          {question.reviewed_at && (
            <Chip
              icon={<CheckCircleIcon />}
              label="已審核"
              color="success"
              size="small"
              variant="outlined"
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {effectiveScore} / {question.maxMarks} 分
          </Typography>
          <QuestionFeedbackChip
            questionId={question.questionId}
            submissionId={submissionId}
            assignmentId={assignmentId}
            aiMarksAwarded={question.marksAwarded}
            aiConfidence={question.confidence}
          />
        </Stack>

        {/* Question text */}
        {questionData?.sectionInstruction && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {questionData.sectionInstruction}
          </Typography>
        )}
        {questionData?.questionText && (
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            {questionData.questionText}
          </Typography>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Crop image — question region so teacher sees full row context */}
        {question.crop_data && (
          <Box sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.50' }}>
            <img
              src={question.crop_data}
              alt={`${question.questionId} 題目裁切圖`}
              style={{ maxWidth: '100%', display: 'block', height: 'auto' }}
            />
          </Box>
        )}

        {/* Student answer — for essays, shown beside the original pages so the
            teacher can check the transcript against the handwriting */}
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          學生作答{annotations.length > 0 ? '（點擊標記查看眉批）' : ''}
        </Typography>
        {isEssay && originalPaths.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '560px minmax(0, 1fr)' },
              gap: 3,
              mt: 0.5,
              mb: 1,
              alignItems: 'start',
            }}
          >
            <Box sx={{ position: { md: 'sticky' }, top: { md: 8 } }}>
              <OriginalPages paths={originalPaths} variant="inline" />
            </Box>
            <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.light' }}>
              <AnnotatedTranscript text={question.transcribedAnswer} annotations={annotations} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 0.5, mb: 1, pl: 1, borderLeft: '3px solid', borderColor: 'primary.light' }}>
            <AnnotatedTranscript text={question.transcribedAnswer} annotations={annotations} />
          </Box>
        )}

        {annotations.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              眉批（{annotations.length}）
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <AnnotationList annotations={annotations} />
            </Box>
          </Box>
        )}

        {/* AI result */}
        <Stack spacing={1} sx={{ mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              AI 評分
            </Typography>
            <Typography variant="body2">
              {question.marksAwarded} / {question.maxMarks} 分
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              AI 評語
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {renderComment(question.feedback) ?? <Typography variant="body2">（無評語）</Typography>}
            </Box>
          </Box>
        </Stack>

        {/* Teacher override section — only for needs_review questions */}
        {(needsReview || isReviewed) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color={needsReview ? 'warning.main' : 'success.main'} fontWeight={700} display="block" sx={{ mb: 1 }}>
              {needsReview ? '老師審核' : '老師已審核'}
            </Typography>
            {isReviewed && !needsReview ? (
              /* Read-only display of teacher override */
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    老師分數
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    {question.teacher_score} / {question.maxMarks} 分
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    老師評語
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {renderComment(question.teacher_feedback) ?? <Typography variant="body2">（沿用 AI 評語）</Typography>}
                  </Box>
                </Box>
              </Stack>
            ) : (
              /* Editable controls */
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    label={`分數（滿分 ${question.maxMarks}）`}
                    type="number"
                    value={editScore}
                    onChange={(e) => setEditScore(e.target.value)}
                    size="small"
                    sx={{ width: 160 }}
                    inputProps={{ min: 0, max: question.maxMarks, step: 0.5 }}
                    error={
                      editScore !== '' &&
                      (isNaN(parseFloat(editScore)) ||
                        parseFloat(editScore) < 0 ||
                        parseFloat(editScore) > question.maxMarks)
                    }
                    helperText={
                      editScore !== '' && parseFloat(editScore) > question.maxMarks
                        ? `不能超過 ${question.maxMarks}`
                        : ''
                    }
                  />
                </Stack>
                <TextField
                  label="評語（可直接沿用 AI 評語）"
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  value={editFeedback}
                  onChange={(e) => setEditFeedback(e.target.value)}
                  size="small"
                />
                <Box>
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={
                      localSaving ||
                      saving ||
                      editScore === '' ||
                      isNaN(parseFloat(editScore)) ||
                      parseFloat(editScore) < 0 ||
                      parseFloat(editScore) > question.maxMarks
                    }
                    onClick={handleSave}
                    startIcon={localSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                  >
                    確認審核
                  </Button>
                </Box>
              </Stack>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}

export function SubmissionReviewDetailPage() {
  const { assignmentId, submissionId } = useParams<{
    assignmentId: string;
    submissionId: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [assignment, setAssignment] = useState<AssignmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSurvey, setShowSurvey] = useState(false);

  useEffect(() => {
    if (!submissionId || !user) return;
    loadData();
  }, [submissionId, user]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/submissions/${submissionId}/review`, {
        headers: { 'x-teacher-id': user!.id },
      });
      if (!res.ok) throw new Error((await res.json()).error || '載入失敗');
      const data = await res.json();
      setSubmission(data.submission);
      setAssignment(data.assignment);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = useCallback(
    async (questionIndex: number, score: number, feedback: string) => {
      setSaving(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/submissions/${submissionId}/review/${questionIndex}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'x-teacher-id': user!.id,
            },
            body: JSON.stringify({ teacher_score: score, teacher_feedback: feedback }),
          },
        );
        if (!res.ok) throw new Error((await res.json()).error || '儲存失敗');
        const result = await res.json();

        // Update local state
        setSubmission((prev) => {
          if (!prev?.grading_result) return prev;
          const questions = [...prev.grading_result.questions];
          questions[questionIndex] = result.question;
          const newHasPending = result.hasPendingReview;
          return {
            ...prev,
            has_pending_review: newHasPending,
            grading_result: {
              ...prev.grading_result,
              questions,
              totalMarks: result.totalMarks,
              maxTotalMarks: result.maxTotalMarks,
            },
          };
        });

        // If this was the last pending review, trigger the post-review survey
        if (result.hasPendingReview === false) {
          setShowSurvey(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '儲存失敗');
      } finally {
        setSaving(false);
      }
    },
    [submissionId, user],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!submission || !assignment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">找不到提交資料</Alert>
      </Box>
    );
  }

  const questions = submission.grading_result?.questions ?? [];
  const extractedQuestions = assignment.wizard_data?.extractedQuestions ?? [];
  const pendingCount = questions.filter((q) => q.needs_review === true && !q.reviewed_at).length;
  const allReviewed = pendingCount === 0 && questions.some((q) => q.reviewed_at);
  // Essay cards render the original beside the transcript, so the standalone
  // panel above would just repeat it.
  const hasEssayQuestion = questions.some(
    (q) => Boolean((q as any).dimension_scores),
  );

  return (
    <Box sx={{ p: 3, maxWidth: hasEssayQuestion ? 1600 : 820, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/assignments/${assignmentId}/pending-reviews`)}
          variant="text"
        >
          返回列表
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          審核 — {submission.student_name || '未知學生'}
        </Typography>
      </Stack>

      {/* Summary bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        {submission.student_class && (
          <Chip label={`班別：${submission.student_class}`} size="small" variant="outlined" />
        )}
        <Chip label={`提交：${formatDate(submission.submitted_at)}`} size="small" variant="outlined" />
        {submission.grading_result && (
          <Chip
            label={`總分：${submission.grading_result.totalMarks} / ${submission.grading_result.maxTotalMarks}`}
            color="primary"
            size="small"
          />
        )}
        {pendingCount > 0 && (
          <Chip label={`${pendingCount} 題待審核`} color="warning" size="small" />
        )}
      </Stack>

      {/* Student's original handwriting — essays have no per-question crop, so
          without this the teacher reviews a transcript with nothing to check it against */}
      {submission.image_paths?.length && !hasEssayQuestion ? (
        <OriginalPages paths={submission.image_paths} />
      ) : null}

      {/* All reviewed banner */}
      {allReviewed && (
        <Alert
          icon={<CheckCircleIcon />}
          severity="success"
          sx={{ mb: 2 }}
        >
          所有題目已審核完成
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Question cards */}
      {questions.length === 0 ? (
        <Alert severity="info">此提交沒有批改結果</Alert>
      ) : (
        questions.map((q, idx) => {
          const questionData = extractedQuestions.find(
            (eq) => eq.questionId === q.questionId,
          );
          return (
            <QuestionCard
              key={q.questionId ?? idx}
              question={q}
              questionIndex={idx}
              questionData={questionData}
              originalPaths={submission.image_paths ?? []}
              submissionId={submissionId!}
              assignmentId={assignmentId!}
              onSave={handleSave}
              saving={saving}
            />
          );
        })
      )}

      {/* Post-review micro-survey — only when VITE_FEEDBACK_MODE=true */}
      <PostReviewSurvey
        open={showSurvey}
        onClose={() => setShowSurvey(false)}
        submissionId={submissionId!}
        assignmentId={assignmentId!}
        totalQuestions={questions.length}
        totalFlagged={0}
      />
    </Box>
  );
}
